import { IconButton } from '@fluentui/react/lib/Button';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import React, { FC } from 'react';

import { useTheme } from '../../util/theme';
import { ButtonColor } from '../Button';
import { IconSize } from '../SVGIcon';
import BaseSVGIcon from '../SVGIcon/BaseSVGIcon';

import { getClassNames, getClassNamesForSVGIcon, getStyles, menuStyles } from './MenuButton.styles';
import { FluentIconMenuButtonItem, MenuButtonItem, MenuButtonProps, SVGIconMenuButtonItem } from './MenuButton.types';

const isFluentIconItem = (props: MenuButtonItem): props is FluentIconMenuButtonItem =>
  !!(props as FluentIconMenuButtonItem).iconName;

const isSVGIconItem = (props: MenuButtonItem): props is SVGIconMenuButtonItem =>
  !!(props as SVGIconMenuButtonItem).SVGIcon;

const getSVGIcon = (SVGIcon: typeof BaseSVGIcon) => () =>
  (
    <span className={getClassNamesForSVGIcon(IconSize.Medium).icon}>
      <SVGIcon size={IconSize.Medium} />
    </span>
  );

const getMenuItemIconProps = (item: MenuButtonItem) => {
  if (isSVGIconItem(item)) {
    return { onRenderIcon: getSVGIcon(item.SVGIcon) };
  }

  if (isFluentIconItem(item)) {
    return { iconProps: { iconName: item.iconName } };
  }
};

const getMenuProps = (menuItems: IContextualMenuItem[]) => {
  const items: IContextualMenuItem[] = menuItems.map((item) => {
    const { text: name, type: itemType, href, isDisabled, key, onClick } = item;

    const iconRenderProps = getMenuItemIconProps(item as MenuButtonItem);

    return {
      key,
      name,
      itemType: itemType as ContextualMenuItemType | undefined,
      onClick: (ev) => {
        if (onClick) {
          onClick(ev);
        }
      },
      disabled: isDisabled,
      href,
      target: href ? '_blank' : undefined,
      ...iconRenderProps,
    };
  });

  return {
    styles: menuStyles,
    items,
    isBeakVisible: false,
  };
};

const MenuButton: FC<MenuButtonProps> = ({
  ariaLabel,
  disabled = false,
  menuItems,
  iconName = 'More',
  className,
  size = 32,
  color = ButtonColor.Tertiary,
  hasBorder = false,
  useTransparentBackground = false,
  onClick,
}) => {
  const theme = useTheme();

  return (
    <span className={getClassNames(className).menuButton}>
      <IconButton
        ariaLabel={ariaLabel}
        disabled={disabled}
        menuProps={getMenuProps(menuItems)}
        iconProps={{ iconName }}
        styles={getStyles(theme, size, hasBorder, useTransparentBackground, color)}
        className="qaMenuButton"
        onMenuClick={onClick}
      />
    </span>
  );
};

export default MenuButton;
